import { FirebaseError } from 'firebase/app';

/** Thrown when a record changed on the server after the form loaded it. */
export class ConflictError extends Error {
  constructor() {
    super('This record was changed by someone else since you opened it. Reload to see the latest version.');
    this.name = 'ConflictError';
  }
}

export class NotFoundError extends Error {
  constructor(what: string) {
    super(`${what} was not found. It may have been deleted.`);
    this.name = 'NotFoundError';
  }
}

const AUTH_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'Email or password is incorrect.',
  // Older codes, returned when email enumeration protection is off (and by the emulator).
  'auth/wrong-password': 'Email or password is incorrect.',
  'auth/user-not-found': 'Email or password is incorrect.',
  'auth/invalid-email': 'Enter a valid email address.',
  'auth/user-disabled': 'This account is disabled. Ask an admin for help.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes or reset your password.',
  'auth/network-request-failed': 'No connection. Check the network and try again.',
  'auth/invalid-verification-code': 'That code is not valid. Check the app and try again.',
  'auth/missing-code': 'Enter the 6-digit code from your authenticator app.',
  'auth/totp-challenge-timeout': 'The sign-in took too long. Start again.',
  'auth/password-does-not-meet-requirements':
    'Your password no longer meets the password policy. Use "Forgot password" to set a new one.',
  'auth/requires-recent-login': 'For security, enter your password again.',
  'auth/unverified-email': 'Your email address must be verified first. Ask an admin.',
};

const FIRESTORE_MESSAGES: Record<string, string> = {
  'permission-denied':
    'You do not have permission for this. If your role changed recently, sign out and back in.',
  unavailable: 'The database is unreachable. Check the network and try again.',
  'deadline-exceeded': 'The request timed out. Try again.',
  'failed-precondition': 'The database is not ready for this query yet. Try again in a few minutes.',
};

export function errorMessage(err: unknown): string {
  if (err instanceof ConflictError || err instanceof NotFoundError) return err.message;
  if (err instanceof FirebaseError) {
    const code = err.code.replace(/^firestore\//, '');
    return AUTH_MESSAGES[err.code] ?? FIRESTORE_MESSAGES[code] ?? `Something went wrong (${err.code}).`;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function isPermissionDenied(err: unknown): boolean {
  return err instanceof FirebaseError && /permission-denied$/.test(err.code);
}
